import React, { useState, useEffect, createContext, useContext } from 'react'
import { Box, Text, HStack, Button, IconButton } from '@chakra-ui/react'
import { FiX, FiEye, FiUser } from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

// Context to provide view-as state to other components
interface ViewAsContextType {
  isViewAsMode: boolean
  adminUid: string | null
  targetUserEmail: string | null
}

const ViewAsContext = createContext<ViewAsContextType>({
  isViewAsMode: false,
  adminUid: null,
  targetUserEmail: null,
})

export const useViewAs = () => useContext(ViewAsContext)

interface ViewAsBannerProps {
  onClose?: () => void
}

export const ViewAsBanner: React.FC<ViewAsBannerProps> = ({ onClose }) => {
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const [isViewAsMode, setIsViewAsMode] = useState(false)
  const [adminUid, setAdminUid] = useState<string | null>(null)
  const [targetUserEmail, setTargetUserEmail] = useState<string | null>(null)

  const contextValue: ViewAsContextType = {
    isViewAsMode,
    adminUid,
    targetUserEmail,
  }

  useEffect(() => {
    const checkViewAsMode = async () => {
      // First check sessionStorage (tab-specific flag set by ViewAsPage)
      const isViewAsFromStorage = sessionStorage.getItem('viewAsMode') === 'true'
      
      if (isViewAsFromStorage) {
        // We're in view-as mode - show banner even if currentUser hasn't updated yet
        setIsViewAsMode(true)
        
        // Get admin UID from sessionStorage (set by ViewAsPage)
        const adminUidFromStorage = sessionStorage.getItem('viewAsAdminUid')
        if (adminUidFromStorage) {
          setAdminUid(adminUidFromStorage)
        }
        
        // Use current user's email if available (which is the target user in view-as mode)
        // If not available yet, we'll update when currentUser changes
        if (currentUser) {
          setTargetUserEmail(currentUser.email || 'Unknown User')
          
          // Try to get admin UID from token claims as backup
          try {
            const idTokenResult = await currentUser.getIdTokenResult()
            const claims = idTokenResult.claims || {}
            
            if (claims.view_as_admin) {
              setAdminUid(claims.view_as_admin as string)
              // Store in sessionStorage for next check
              sessionStorage.setItem('viewAsAdminUid', claims.view_as_admin as string)
            }
          } catch (error) {
            console.error('Error getting view-as claims:', error)
          }
        } else {
          // User not loaded yet, but we're in view-as mode - show banner with placeholder
          setTargetUserEmail('Loading...')
        }
      } else {
        setIsViewAsMode(false)
        setAdminUid(null)
        setTargetUserEmail(null)
      }
    }

    checkViewAsMode()
    
    // Poll sessionStorage in case it changes (since storage event only fires cross-tab)
    // Use a ref to track the previous state to avoid unnecessary re-renders
    const interval = setInterval(() => {
      const currentFlag = sessionStorage.getItem('viewAsMode') === 'true'
      // Always check if the flag changed, regardless of current state
      checkViewAsMode()
    }, 500) // Check every 500ms
    
    return () => {
      clearInterval(interval)
    }
  }, [currentUser]) // Removed isViewAsMode from dependencies to prevent re-render loops

  const handleExitViewAs = () => {
    // Clear view-as mode flag
    sessionStorage.removeItem('viewAsMode')
    sessionStorage.removeItem('viewAsTargetId')
    
    // Close the current tab/window to exit view-as mode
    // The admin will return to their original session in the admin tab
    window.close()
  }

  if (!isViewAsMode) {
    return (
      <ViewAsContext.Provider value={contextValue}>
        {null}
      </ViewAsContext.Provider>
    )
  }

  return (
    <ViewAsContext.Provider value={contextValue}>
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        zIndex="toast"
        bg="blue.600"
        _dark={{ bg: 'blue.800', borderColor: 'blue.600' }}
        color="white"
        px={4}
        py={3}
        borderTop="2px solid"
        borderColor="blue.700"
        boxShadow="lg"
      >
        <HStack justify="space-between" align="center" maxW="7xl" mx="auto">
          <HStack gap={3} align="center">
            <Box
              as={FiEye}
              boxSize={5}
              color="white"
              opacity={0.95}
            />
            <HStack gap={2} align="baseline">
              <Text fontWeight="semibold" fontSize="sm" lineHeight="shorter">
                Viewing as: <Text as="span" fontWeight="bold">{targetUserEmail}</Text>
              </Text>
              <Text 
                fontSize="xs" 
                opacity={0.75} 
                _dark={{ opacity: 0.85 }}
                color="whiteAlpha.900"
              >
                (Admin: {adminUid?.substring(0, 8)}...)
              </Text>
            </HStack>
          </HStack>
          
          <HStack gap={2}>
            <Button
              size="sm"
              variant="solid"
              colorScheme="whiteAlpha"
              bg="whiteAlpha.200"
              color="white"
              borderWidth="1px"
              borderColor="whiteAlpha.300"
              _hover={{ 
                bg: 'whiteAlpha.300',
                borderColor: 'whiteAlpha.400',
                transform: 'translateY(-1px)'
              }}
              _dark={{ 
                bg: 'whiteAlpha.200',
                borderColor: 'whiteAlpha.300',
                _hover: { 
                  bg: 'whiteAlpha.300',
                  borderColor: 'whiteAlpha.400'
                }
              }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.2s"
              onClick={() => {
                // Navigate to account page to show current user info
                navigate('/account')
              }}
            >
              <FiUser style={{ marginRight: '6px' }} />
              Account Info
            </Button>
            <IconButton
              aria-label="Exit view-as mode"
              size="sm"
              variant="solid"
              colorScheme="whiteAlpha"
              bg="whiteAlpha.200"
              color="white"
              borderWidth="1px"
              borderColor="whiteAlpha.300"
              _hover={{ 
                bg: 'red.500',
                borderColor: 'red.400',
                transform: 'translateY(-1px)'
              }}
              _dark={{ 
                bg: 'whiteAlpha.200',
                borderColor: 'whiteAlpha.300',
                _hover: { 
                  bg: 'red.600',
                  borderColor: 'red.500'
                }
              }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.2s"
              onClick={handleExitViewAs}
            >
              <FiX />
            </IconButton>
          </HStack>
        </HStack>
      </Box>
    </ViewAsContext.Provider>
  )
}

export default ViewAsBanner
